<template>
  <BContent
    v-loading="loading"
  >
    <template #navbar>
      <BLayout
        align-start
        justify-start
      >
        <span class="fs-400 fw-bold">{{ i18n.t('training.title') }}</span>
      </BLayout>
    </template>
    <template #waiting>
      <BPlaceholder
        v-loading="true"
        element-loading-background="transparent"
      />
    </template>
    <BTabs
      v-model="activeName"
      class="tabs"
      :label-items="tabLabelItems"
    >
      <template #callFb>
        <SisenseContextProvider
          :url="sisenseUrl"
          :app-config="sisenseAppConfig"
          sso-enabled
        >
          <template v-if="sisenseAuthChecked">
            <CallFb
              v-model:caller-id="callerId"
              v-model:start-date="startDate"
              v-model:end-date="endDate"
              v-model:default-call-result-type-id="defaultCallResultTypeId"
              v-model:custom-call-result-type-id="customCallResultTypeId"
              v-model:call-connection-point="callConnectionPoint"
              v-model:call-result-id="callResultId"
            />
          </template>
        </SisenseContextProvider>
      </template>
    </BTabs>
  </BContent>
</template>

<script lang="ts" setup>
import {
  SisenseContextProvider,
} from '@sisense/sdk-ui-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CallFb from '@/components/organisms/user/callFb/CallFb.vue';
import { useQueryParameter } from '@/composable/query-parameter';
import { useSisense } from '@/composable/user/sisense/sisense';

const { sisenseAuthChecked } = useSisense();

const sisenseUrl = `${import.meta.env.VITE_SISENSE_URL}`;
const sisenseAppConfig = {
  dateConfig: {
    timeZone: 'JST',
  },
};

const i18n = useI18n();
const { param: callerIdParam } = useQueryParameter<number>('callerId', (param) => Number(param) || undefined);
const { param: startDateParam } = useQueryParameter<string>('startDate');
const { param: endDateParam } = useQueryParameter<string>('endDate');
const { param: defaultCallResultTypeIdParam } = useQueryParameter<number>('defaultCallResultTypeId', (param) => Number(param) || null);
const { param: customCallResultTypeIdParam } = useQueryParameter<number>('customCallResultTypeId', (param) => Number(param) || null);
const { param: callConnectionPointParam } = useQueryParameter<string>('callConnectionPoint');
const { param: callResultIdParam } = useQueryParameter<number>('callResultId', (param) => Number(param) || null);

// NOTE: 「すべて〜」など未選択状態となった時に、クエリパラメータから消す。
const callerId = computed({
  get: () => callerIdParam.value,
  set: (value: number | undefined) => {
    callerIdParam.value = value ?? undefined;
  },
});
const startDate = computed({
  get: () => startDateParam.value,
  set: (value: string | undefined) => {
    startDateParam.value = value ?? undefined;
  },
});
const endDate = computed({
  get: () => endDateParam.value,
  set: (value: string | undefined) => {
    endDateParam.value = value ?? undefined;
  },
});
const defaultCallResultTypeId = computed({
  get: () => defaultCallResultTypeIdParam.value,
  set: (value: number | null) => {
    defaultCallResultTypeIdParam.value = value ?? undefined;
  },
});
const customCallResultTypeId = computed({
  get: () => customCallResultTypeIdParam.value,
  set: (value: number | null) => {
    customCallResultTypeIdParam.value = value ?? undefined;
  },
});
const callConnectionPoint = computed({
  get: () => callConnectionPointParam.value,
  set: (value: string | null) => {
    callConnectionPointParam.value = value ?? undefined;
  },
});
const callResultId = computed({
  get: () => callResultIdParam.value,
  set: (value: number | null) => {
    callResultIdParam.value = value ?? undefined;
  },
});

const activeName = ref('callFb');
const loading = computed(() => false);

const tabLabelItems = [
  { label: i18n.t('training.callFb.title'), slotName: 'callFb' },
];
</script>

<style lang="scss" scoped>
.tabs {
  height: 100%;
}

:deep(.el-tabs__header) {
  margin-bottom: 0;

  .el-tabs__nav-scroll {
    padding: 0 $basespace-600;
  }
}

:deep(.el-tabs__content) {
  // NOTE: header分引く
  height: calc(100% - 40px);

  .el-tab-pane {
    height: 100%;
  }
}
</style>
